import React, { useState, useCallback, ReactNode, useRef } from 'react';

import cls from 'classnames';

import { Container } from '@ott/grid';
import ChevronDownIcon from '@ott/icon-chevron-down';
import OTTLogoWithText from '@ott/icon-ott-logo-with-text';
import IconTripcoin from '@ott/icon-tripcoin';
import { l10n } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';
import addScpParameterToUrl from '@ott/utility-scp-logic';

import useMenuItems from './hooks/useMenuItems';
import useOnClickOutside from './hooks/useOnClickOutside';
import { MenuItem } from './types';

import styles from './Header.scss';

const DEFAULT_HREF_KEY = 'href';
const MOBILE_HREF_KEY = 'mobileHref';
const PROTOCOL_PAT = /^(http|https|\/\/)/;
const ITEMS_WITH_CASHBACK_PERCENT = [{ product: 'hotels', percent: 15 }];
const ITEMS_WITH_UPPER_LINE = ['b2b', 'explore', 'hc'];

const tripcoinClassNames = {
  circle: styles.iconCircle,
  text: styles.iconText,
};

export type Props = {
  /**
   * Если прокидываем лого извне, нужно следить, чтобы размер был 40x40пкс, чтобы соответствовать макету
   */
  logo?: ReactNode;
  /**
   * Сюда прокидывается компонент `<LanguageSwitcher/>.` Проп `showOnlyFlag` компонента `<LanguageSwitcher/>` должен быть `true`, чтобы соответствовать макету
   */
  languageSwitcher?: ReactNode;
  auth?: ReactNode;
  lang?: string;
  hostname?: string;
  /**
   * Если передается проп `partner` и в нем есть `enabledMenuItems`, то меню формируется из `enabledMenuItems`
   * и проп `hiddenMenuItems` будет проигнорирован
   */
  partner?: { [key: string]: any };
  /**
   * Массив айтемов меню, которые нужно спрятать
   */
  hiddenMenuItems?: MenuItem[];
  /**
   * Указываем какой айтем должен быть активным
   */
  selectedItem?: MenuItem;
  className?: string;
  onMenuItemClick?: (item: string) => void;
};

const Header = ({
  // @ts-ignore
  logo = <OTTLogoWithText to="/" iconWidth={40} />,
  languageSwitcher,
  lang = 'ru',
  hostname,
  auth,
  partner,
  hiddenMenuItems,
  selectedItem,
  className,
  onMenuItemClick,
}: Props): JSX.Element => {
  const { isDesktop } = useLayoutContext();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const {
    availableMenuItems: allMenuItems,
    mainMenuItems,
    extraMenuItems,
  } = useMenuItems({
    enabledMenuItems: partner?.enabledMenuItems,
    hiddenMenuItems,
    lang,
  });

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const burgerButtonRef = useRef<HTMLDivElement>(null);

  useOnClickOutside({
    contentRef: mobileMenuRef,
    targetRef: burgerButtonRef,
    handler: () => setIsMobileMenuOpen(false),
    isOpen: isMobileMenuOpen,
    isDisabled: false,
  });

  const navItemsDropDownRef = useRef<HTMLDivElement>(null);
  const businessNavItemRef = useRef<HTMLDivElement>(null);

  useOnClickOutside({
    contentRef: navItemsDropDownRef,
    targetRef: businessNavItemRef,
    handler: () => setIsDropDownOpen(false),
    isOpen: isDropDownOpen,
    isDisabled: false,
  });

  const getMenuItemLink = useCallback(
    ({ item, hrefKey = DEFAULT_HREF_KEY }: { item: MenuItem; hrefKey?: string }) => {
      let url = l10n(`header.${item}.${hrefKey}`);

      if (!url) {
        url = l10n(`header.${item}.${DEFAULT_HREF_KEY}`);
      }

      if (hostname && !PROTOCOL_PAT.test(url)) {
        url = `${hostname}${url}`;
      }

      // для b2b ссылок, нужно прокидывать параметры c scp (если они есть в урле)
      if (['b2b', 'extranet', 'partners'].includes(item)) {
        url = addScpParameterToUrl(url);
      }

      return url;
    },
    [hostname]
  );

  const renderCashbackLabel = ({
    item,
    shouldExpandLabel,
  }: {
    item: MenuItem;
    shouldExpandLabel?: boolean;
  }) => {
    const itemWithCashback = ITEMS_WITH_CASHBACK_PERCENT.find(({ product }) => product === item);

    if (partner || lang !== 'ru' || !itemWithCashback) {
      return null;
    }

    if (shouldExpandLabel) {
      return (
        <div className={styles.label}>
          <IconTripcoin className={styles.tripcoinIcon} childrenClassNames={tripcoinClassNames} />
          <span className={styles.labelText}>
            {l10n('header.virtualCard.cashback', { percent: itemWithCashback.percent })}
          </span>
        </div>
      );
    }

    return <IconTripcoin className={styles.tripcoinIcon} childrenClassNames={tripcoinClassNames} />;
  };

  const renderMenuItem = ({
    item,
    navItemClassName,
    hrefKey,
    shouldExpandLabel,
  }: {
    item: MenuItem;
    navItemClassName: string;
    hrefKey?: string;
    shouldExpandLabel?: boolean;
  }) => {
    const menuItemTextContent = l10n(`header.${item}.text`);
    const menuItemDescriptionContent = l10n(`header.${item}.description`);

    if (!menuItemTextContent) {
      return null;
    }

    const handleMenuItemClick = () => {
      if (onMenuItemClick) {
        onMenuItemClick(item);
      }
    };

    return (
      <a
        key={item}
        className={cls(navItemClassName, {
          [styles.isActive]: item === selectedItem,
          [styles.withUpperLine]: ITEMS_WITH_UPPER_LINE.includes(item),
          [styles.descriptionNavItem]: menuItemDescriptionContent,
        })}
        href={getMenuItemLink({ item, hrefKey })}
        onClick={handleMenuItemClick}
      >
        <span data-locator="menuItemText" className={styles.menuItemText}>
          {menuItemTextContent}
        </span>
        {renderCashbackLabel({ item, shouldExpandLabel })}
        {menuItemDescriptionContent && (
          <div data-locator="menuItemDescription" className={styles.menuItemDescription}>
            {menuItemDescriptionContent}
          </div>
        )}
      </a>
    );
  };

  const renderMainNavItems = () => {
    const navItems = mainMenuItems.map((item) =>
      renderMenuItem({ item, navItemClassName: styles.navItem })
    );

    return (
      <>
        {navItems.slice(0, 7)}
        <div
          role="presentation"
          className={styles.navItem}
          ref={businessNavItemRef}
          onClick={() => setIsDropDownOpen(!isDropDownOpen)}
        >
          <div className={cls(styles.menuItemText, styles.businessNavItem)}>
            {l10n('header.business.text')}
            <ChevronDownIcon
              className={cls(styles.arrow, {
                [styles.isOpen]: isDropDownOpen,
              })}
            />
          </div>
        </div>
        {isDropDownOpen && (
          <div ref={navItemsDropDownRef} className={styles.dropDown}>
            {navItems.slice(7)}
          </div>
        )}
      </>
    );
  };

  const renderExtraNavItems = () => {
    return (
      <div className={styles.extraNavItems}>
        {extraMenuItems.map((item) =>
          renderMenuItem({
            item,
            navItemClassName: styles.navItem,
          })
        )}
      </div>
    );
  };

  const renderMobileNavItems = () =>
    allMenuItems.map((item) =>
      renderMenuItem({
        item,
        navItemClassName: styles.mobileMenuItem,
        hrefKey: MOBILE_HREF_KEY,
        shouldExpandLabel: true,
      })
    );

  const renderMobileMenu = () => (
    <div
      ref={mobileMenuRef}
      className={cls(styles.mobileMenu, {
        [styles.isMobileMenuOpen]: isMobileMenuOpen,
      })}
      data-locator="mobileMenu"
    >
      {renderMobileNavItems()}
    </div>
  );

  const renderBurgerButton = () => (
    <div
      ref={burgerButtonRef}
      className={cls(styles.burgerButtonWrapper, {
        [styles.burgerButtonWrapperHidden]: allMenuItems.length <= 1,
      })}
    >
      <div
        role="presentation"
        className={styles.burgerButton}
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        data-locator="mobileMenuButton"
      >
        <div className={cls(styles.burger, { [styles.isMobileMenuOpen]: isMobileMenuOpen })}>
          <div className={styles.burgerBox}>
            <div className={styles.burgerInner} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <header className={cls(styles.Header, className)}>
      <Container className={styles.container}>
        {renderBurgerButton()}
        {logo && <div className={styles.logo}>{logo}</div>}
        {isDesktop && <nav className={styles.mainMenu}>{renderMainNavItems()}</nav>}
        <div className={styles.extraMenu}>
          {renderExtraNavItems()}
          {auth && <div className={styles.auth}>{auth}</div>}
          {isDesktop && languageSwitcher && (
            <div className={styles.language}>{languageSwitcher}</div>
          )}
        </div>
      </Container>
      {renderMobileMenu()}
    </header>
  );
};

export default Header;
